import {endsWith, isNil} from 'lodash-es';
import {MAX_QUANTITY_VALUE} from '../../common/constants';
import {createNumberFormat} from '../../common/formatters';
import {AccountType} from '../../enums/account-type';
import {AssetClass} from '../../enums/asset-class';
import {AssetType, getSecurityAssetByType} from '../../enums/asset-type';
import {Exchange} from '../../enums/exchange';
import {OrdType} from '../../enums/order-type';
import {Side} from '../../enums/side';
import {NewOrder, Order} from '../../models/order';
import {Report} from '../../models/report';
import {Security} from '../../models/security';
import {Position} from '../position';
import {SwitchState} from '../switch';
import {
    createDisplayDecimal,
    DisplayDecimalWithCurrency,
    formatPrice,
    formatQuantity,
    SymbolFormats,
    ValueFormatter
} from '../symbol-decimals';
import {
    OrderQtyDecimalsInputParams,
    OrderQtyMultiCurrencyInfo,
    OrderQtyMultiCurrencyInfoExtraArgs
} from './order-qty-multi-currency-info';

export const DEFAULT_ORDER_QTY_ASSET_TYPE: AssetType = AssetType.BASE;

export const ORDER_QTY_ASSET_TYPE_LABEL: string = 'Currency'; // TODO: translate

export interface Volume24hCurrencyInfo {
    assetType?: AssetType;
    fixedCurrencySymbol?: string;
    fixedCurrencyScale?: number;
}

const ASSET_CLASS_SPOT = AssetClass.SPOT;
const ASSET_CLASS_PER = AssetClass.PER;
const ASSET_CLASS_FUT = AssetClass.FUT;
const SYMBOL_BTC = 'BTC';
const SYMBOL_USD = 'USD';
const SYMBOL_USDT = 'USDT';
const SYMBOL_USDC = 'USDC';

export const SYMBOL_CONTRACTS = 'Cont'; // TODO: Translate?

const QTY_INPUT_PARAMS_FOUR_DECIMAL_DIGITS: OrderQtyDecimalsInputParams = {
    decimalCount:      4,
    decimalStoreCount: 4,
    step:              0.0001,
    min:               0.0001,
    max:               MAX_QUANTITY_VALUE,
    precision:         undefined
};

const QTY_INPUT_PARAMS_TWO_DECIMAL_DIGITS: OrderQtyDecimalsInputParams = {
    decimalCount:      2,
    decimalStoreCount: 2,
    step:              0.01,
    min:               0.01,
    max:               MAX_QUANTITY_VALUE,
    precision:         undefined
};

export type OrderOrReportForQtyMultiCurrency = (Order | NewOrder | Report)
    & Partial<Pick<Order, 'orderQty' | 'orderQuoteQty' | '_orderBaseQty'>>;

const isMultiCollateralPerp = (security: Security): boolean =>
    security.assetClass === ASSET_CLASS_PER
    && endsWith(security.symbol, '.MC.P');

// tslint:disable:cyclomatic-complexity
export function getOrderQtyMultiCurrencyInfo(
    security: Security,
    extraArgs: OrderQtyMultiCurrencyInfoExtraArgs
): OrderQtyMultiCurrencyInfo {
    let result: OrderQtyMultiCurrencyInfo;
    const exchange = security.securityExchange;
    const {assetClass} = security;
    extraArgs = extraArgs || {} as OrderQtyMultiCurrencyInfoExtraArgs;

    const getCurrencySwitchItems = (): SwitchState[] => [
        AssetType.BASE,
        AssetType.QUOTE
    ].map<SwitchState>(assetType => {
        const asset = getSecurityAssetByType(security, assetType);

        return {
            value: assetType,
            label: asset.symbol
        };
    });

    if (exchange === Exchange.TRADIAS) {

        // [TRAD-154] Disable qty in quote asset, if contractMultiplier > 1.
        const isQuoteDisabledByContractMultiplier = security.contractMultiplier > 1;

        result = isQuoteDisabledByContractMultiplier
            ? {
                isMultiCurrencyAllowed: false,
                baseQtyAssetType:       DEFAULT_ORDER_QTY_ASSET_TYPE
            }
            : {
                isMultiCurrencyAllowed:           !isQuoteDisabledByContractMultiplier,
                baseQtyAssetType:                 DEFAULT_ORDER_QTY_ASSET_TYPE,
                currencySwitchItems:              isQuoteDisabledByContractMultiplier
                                                      ? undefined
                                                      : getCurrencySwitchItems(),
                isMultiCurrencyNativelySupported: !isQuoteDisabledByContractMultiplier,
                fixedDecimalParamsByAssetType:    {

                    /**
                     * [TRAD-101] Tradias requires qty in quote as with max 2 decimal digits.
                     * We decided to hardcode it.
                     */
                    [AssetType.QUOTE]: QTY_INPUT_PARAMS_TWO_DECIMAL_DIGITS
                }
            };
    } else if (exchange === Exchange.DERIBIT) {
        if (assetClass === ASSET_CLASS_PER || assetClass === ASSET_CLASS_FUT) {
            const {quoteAsset} = security;

            /**
             * [HED-840] Securities with quoteAsset USDC are exceptions,
             * their qty should be in baseAsset.
             */
            if (quoteAsset.symbol !== SYMBOL_USDC) {
                result = {
                    isMultiCurrencyAllowed:        true,
                    baseQtyAssetType:              AssetType.QUOTE,
                    currencySwitchItems:           getCurrencySwitchItems(),
                    fixedDecimalParamsByAssetType: {

                        /**
                         * [HED-274] For Deribit we decided to format qty in alternative (view only) currency
                         * with these hardcoded parameters.
                         */
                        [AssetType.BASE]: QTY_INPUT_PARAMS_FOUR_DECIMAL_DIGITS
                    }
                };
            }
        }
    } else if (exchange === Exchange.BITMEX) {
        const {baseAsset, quoteAsset} = security;

        if (quoteAsset.symbol !== SYMBOL_USDT) {
            if (baseAsset.symbol === SYMBOL_BTC) {
                result = {
                    isMultiCurrencyAllowed: true,
                    baseQtyAssetType:       AssetType.QUOTE,
                    currencySwitchItems:    getCurrencySwitchItems(),
                    fixedDecimalParamsByAssetType: {

                        /**
                         * BitMEX has term "Order Value" in base asset currency and scale = 4
                         */
                        [AssetType.BASE]: QTY_INPUT_PARAMS_FOUR_DECIMAL_DIGITS
                    }
                };
            } else if (quoteAsset.symbol !== SYMBOL_BTC) {
                result = {
                    isMultiCurrencyAllowed: false,
                    baseQtyAssetType:       DEFAULT_ORDER_QTY_ASSET_TYPE,
                    isQtyInputInContracts:  true
                };
            }
        }
    } else if (exchange === Exchange.FTX || exchange === Exchange.FTX_US) {
        result = {
            isMultiCurrencyAllowed: true,
            baseQtyAssetType:       DEFAULT_ORDER_QTY_ASSET_TYPE,
            currencySwitchItems:    getCurrencySwitchItems()
        };
    } else if (exchange === Exchange.BINANCE_FUT && security.currency === SYMBOL_USD) { // HED-486
        result = {
            isMultiCurrencyAllowed: false,
            baseQtyAssetType:       DEFAULT_ORDER_QTY_ASSET_TYPE,
            isQtyInputInContracts:  true
        };
    } else if (exchange === Exchange.KRAKEN_FUT) { // HED-461, HED-863
        if (!isMultiCollateralPerp(security)) {
            result = {
                isMultiCurrencyAllowed: false,
                baseQtyAssetType:       DEFAULT_ORDER_QTY_ASSET_TYPE,
                isQtyInputInContracts:  true
            };
        }
    } else if (exchange === Exchange.OKEX) { // HED-786
        if (assetClass === ASSET_CLASS_PER || assetClass === ASSET_CLASS_FUT) {
            result = {
                isMultiCurrencyAllowed: false,
                baseQtyAssetType:       DEFAULT_ORDER_QTY_ASSET_TYPE,
                isQtyInputInContracts:  true
            };
        } else if (assetClass === ASSET_CLASS_SPOT) {
            if (
                extraArgs.orderType === OrdType.MARKET
                && extraArgs.side === Side.BUY
                && extraArgs.accountType === AccountType.MARGIN
            ) {
                result = {
                    isMultiCurrencyAllowed: true,
                    baseQtyAssetType:       AssetType.QUOTE,
                    currencySwitchItems:    getCurrencySwitchItems()
                };
            }
        }
    }

    result = result || getDefaultOrderQtyMultiCurrencyInfo();

    return result;
}

export function getDefaultOrderQtyMultiCurrencyInfo(): OrderQtyMultiCurrencyInfo {
    return {
        isMultiCurrencyAllowed: false,
        baseQtyAssetType:       DEFAULT_ORDER_QTY_ASSET_TYPE
    };
}

/**
 * Returns symbol of the base qty asset
 */
export function getOrderQtyCurrency(
    security: Security,
    qtyMultiCurrencyInfo: OrderQtyMultiCurrencyInfo,
    qtyAssetType?: AssetType
): string {
    let currencyStr: string;

    qtyAssetType = qtyAssetType || qtyMultiCurrencyInfo.baseQtyAssetType;

    const isBaseQtyAssetType = qtyAssetType === qtyMultiCurrencyInfo.baseQtyAssetType;

    if (qtyMultiCurrencyInfo.isQtyInputInContracts && isBaseQtyAssetType) {
        currencyStr = SYMBOL_CONTRACTS;
    } else {
        const qtyAsset = getSecurityAssetByType(security, qtyAssetType);

        currencyStr = qtyAsset && qtyAsset.symbol;
    }

    return currencyStr || '';
}

/**
 * Returns symbol of the base qty asset as a suffix after a number
 */
export function getOrderQtyCurrencySuffix(security: Security, qtyMultiCurrencyInfo: OrderQtyMultiCurrencyInfo): string {
    const currencyStr = getOrderQtyCurrency(security, qtyMultiCurrencyInfo);

    return currencyStr && ` ${currencyStr}`;
}

// tslint:disable:cyclomatic-complexity
export function getVolume24hCurrencyInfo(
    security: Security,
    extraArgs?: OrderQtyMultiCurrencyInfoExtraArgs // Assume, that extraArgs are not important for volume24
): Volume24hCurrencyInfo {
    let result: Volume24hCurrencyInfo;
    const exchange = security.securityExchange;
    const {assetClass} = security;

    if (exchange === Exchange.BITMEX || exchange === Exchange.DERIBIT) {
        const orderQtyMultiCurrencyInfo = getOrderQtyMultiCurrencyInfo(security, extraArgs);

        if (orderQtyMultiCurrencyInfo.isQtyInputInContracts) {
            result = {
                fixedCurrencyScale:  0,
                fixedCurrencySymbol: SYMBOL_CONTRACTS
            };
        } else {
            const qtyAsset = getSecurityAssetByType(security, orderQtyMultiCurrencyInfo.baseQtyAssetType);

            result = {
                assetType:           orderQtyMultiCurrencyInfo.baseQtyAssetType,
                fixedCurrencySymbol: qtyAsset && qtyAsset.symbol || ''
            };

            if (exchange === Exchange.DERIBIT) {
                result.fixedCurrencyScale = 0;
            }
        }
    } else if (exchange === Exchange.BINANCE || exchange === Exchange.BINANCE_FUT || exchange === Exchange.BINANCE_US) {
        result = {
            assetType:          AssetType.QUOTE,
            fixedCurrencyScale: 2
        };
    } else if (exchange === Exchange.BITSTAMP) {
        result = {
            assetType:          AssetType.QUOTE,
            fixedCurrencyScale: 0
        };
    } else if (exchange === Exchange.FTX || exchange === Exchange.FTX_US) {
        result = {
            fixedCurrencySymbol: SYMBOL_USD,
            fixedCurrencyScale: 2
        };
    } else if (exchange === Exchange.KRAKEN) {
        result = {
            assetType:          AssetType.QUOTE,
            fixedCurrencyScale: 0
        };
    } else if (exchange === Exchange.KRAKEN_FUT) { // HED-461, HED-863
        if (isMultiCollateralPerp(security)) {
            result = {
                assetType:          AssetType.BASE,
                fixedCurrencyScale: 0
            };
        } else {
            result = {
                fixedCurrencySymbol: SYMBOL_CONTRACTS,
                fixedCurrencyScale:  0
            };
        }
    } else if (exchange === Exchange.OKEX) { // HED-766
        if (assetClass === ASSET_CLASS_PER || assetClass === ASSET_CLASS_FUT) {
            result = {
                fixedCurrencyScale:  0,
                fixedCurrencySymbol: SYMBOL_CONTRACTS
            };
        } else if (assetClass === ASSET_CLASS_SPOT) {
            result = {
                fixedCurrencyScale: 0,
                assetType:          AssetType.BASE
            };
        }
    }

    return result || {
        assetType: AssetType.QUOTE
    };
}

export function createVolume24hFormatter(
    security: Security,
    symbolFormats: SymbolFormats
): (value: number) => DisplayDecimalWithCurrency {
    const volume24hCurrencyInfo = getVolume24hCurrencyInfo(security);
    let formatterNoCurrency: ValueFormatter;
    let currencySymbol: string;

    if (isNil(volume24hCurrencyInfo.fixedCurrencyScale)) {
        formatterNoCurrency = (volume24hCurrencyInfo.assetType === AssetType.QUOTE)
            ? value => formatPrice(value, symbolFormats)
            : value => formatQuantity(value, symbolFormats);
    } else {
        const numberFormat = createNumberFormat({
            isDecimalRequired: true,
            decimalCount:      volume24hCurrencyInfo.fixedCurrencyScale
        });

        formatterNoCurrency = value => createDisplayDecimal(value, numberFormat);
    }

    if (!volume24hCurrencyInfo.fixedCurrencySymbol) {
        const asset = getSecurityAssetByType(security, volume24hCurrencyInfo.assetType);
        currencySymbol = asset && asset.symbol || '';
    } else {
        currencySymbol = volume24hCurrencyInfo.fixedCurrencySymbol;
    }

    return value => {
        const displayDecimal = formatterNoCurrency(value) as DisplayDecimalWithCurrency;

        displayDecimal.currencySymbol = currencySymbol;

        return displayDecimal;
    };
}

export function correctQtyAssetType(
    security: Security,
    qtyAssetType: AssetType,
    extraArgs: OrderQtyMultiCurrencyInfoExtraArgs
): AssetType {
    const orderQtyMultiCurrencyInfo = security && getOrderQtyMultiCurrencyInfo(security, extraArgs);

    if (orderQtyMultiCurrencyInfo) {
        const allowedValues = orderQtyMultiCurrencyInfo.currencySwitchItems
            ? orderQtyMultiCurrencyInfo.currencySwitchItems.map(item => item.value)
            : [orderQtyMultiCurrencyInfo.baseQtyAssetType];

        if (!allowedValues.includes(qtyAssetType)) {
            qtyAssetType = orderQtyMultiCurrencyInfo.baseQtyAssetType || DEFAULT_ORDER_QTY_ASSET_TYPE;
        }
    } else {
        qtyAssetType = DEFAULT_ORDER_QTY_ASSET_TYPE;
    }

    return qtyAssetType;
}

type GetOrderQtyMultiCurrencyExtraArgsFromOrderArgType = Pick<Order | NewOrder | Report,
    'side' | 'ordType' | 'leverage'>;
export const getOrderQtyMultiCurrencyExtraArgsFromOrder = (
    orderOrReport: GetOrderQtyMultiCurrencyExtraArgsFromOrderArgType
): OrderQtyMultiCurrencyInfoExtraArgs => ({

    /**
     * The same condition as in {@link getAccountBaseByNewOrderParams}
     */
    accountType: orderOrReport.leverage ? AccountType.MARGIN : AccountType.CASH,

    orderType: orderOrReport.ordType,
    side:      orderOrReport.side
});

type GetOrderQtyMultiCurrencyExtraArgsFromPositionArgType = Pick<Position, 'quantity'>;
export const getOrderQtyMultiCurrencyExtraArgsFromPosition = (
    position: GetOrderQtyMultiCurrencyExtraArgsFromPositionArgType
): OrderQtyMultiCurrencyInfoExtraArgs => ({

    /**
     * Assume, that it's always MARGIN for positions.
     */
    accountType: AccountType.MARGIN,

    /**
     * Assume, that it's always MARKET for positions.
     */
    orderType: OrdType.MARKET,

    /**
     * Side of opposite order.
     */
    side:      position.quantity > 0 ? Side.SELL : Side.BUY
});

export function getOrderQtyMultiCurrencyInfoForOrder(
    newOrder: NewOrder,
    security: Security // NewOrder may not have full Security, so security arg is required.
): OrderQtyMultiCurrencyInfo;
export function getOrderQtyMultiCurrencyInfoForOrder(
    orderOrReport: Order | Report,
    security?: Security
): OrderQtyMultiCurrencyInfo;
export function getOrderQtyMultiCurrencyInfoForOrder(
    orderOrReport: GetOrderQtyMultiCurrencyExtraArgsFromOrderArgType
        & Partial<Pick<Order | NewOrder | Report, 'security'>>,
    security?: Security
): OrderQtyMultiCurrencyInfo {
    return getOrderQtyMultiCurrencyInfo(
        security || orderOrReport.security,
        getOrderQtyMultiCurrencyExtraArgsFromOrder(orderOrReport)
    );
}

export function getOrderQtyMultiCurrencyInfoForPosition(
    position: GetOrderQtyMultiCurrencyExtraArgsFromPositionArgType & Partial<Pick<Position, 'security'>>,
    security?: Security
): OrderQtyMultiCurrencyInfo {
    return getOrderQtyMultiCurrencyInfo(
        security || position.security,
        getOrderQtyMultiCurrencyExtraArgsFromPosition(position)
    );
}

/**
 * [TRAD-101, TRAD-114] Returns asset type of qty in the order.
 */
export function getOrderQtyAssetType(
    security: Security,
    orderOrReport: OrderOrReportForQtyMultiCurrency,
    qtyMultiCurrencyInfo?: OrderQtyMultiCurrencyInfo,
    bypassNativeSupport: boolean = false,
    forcedQtyAssetType?: AssetType
): AssetType {
    qtyMultiCurrencyInfo = qtyMultiCurrencyInfo
        || getOrderQtyMultiCurrencyInfoForOrder(orderOrReport as NewOrder, security);

    return qtyMultiCurrencyInfo.isMultiCurrencyAllowed
        ? forcedQtyAssetType
        || (
            (
                (bypassNativeSupport || qtyMultiCurrencyInfo.isMultiCurrencyNativelySupported)
                && !isNil(orderOrReport.orderQuoteQty)
            )
                ? AssetType.QUOTE

                // Usually this field is empty, but present only, when it must to me used first.
                : !isNil(orderOrReport._orderBaseQty)

                    ? AssetType.BASE
                    : qtyMultiCurrencyInfo.baseQtyAssetType
        )
        : qtyMultiCurrencyInfo.baseQtyAssetType;
}

/**
 * [TRAD-101, TRAD-114] Chooses qty from three fields {@link Order.orderQty}, {@link Order.orderQuoteQty}
 * and {@link Order._orderBaseQty}.
 */
export function getQtyFromOrder(
    security: Security,
    orderOrReport: OrderOrReportForQtyMultiCurrency,
    qtyMultiCurrencyInfo?: OrderQtyMultiCurrencyInfo,
    bypassNativeSupport: boolean = false,
    forcedQtyAssetType?: AssetType
): number {
    qtyMultiCurrencyInfo = qtyMultiCurrencyInfo
        || getOrderQtyMultiCurrencyInfoForOrder(orderOrReport as NewOrder, security);

    return qtyMultiCurrencyInfo.isMultiCurrencyAllowed
        ? (
            (
                bypassNativeSupport
                || qtyMultiCurrencyInfo.isMultiCurrencyNativelySupported
                || forcedQtyAssetType === AssetType.QUOTE
            )
            && !isNil(orderOrReport.orderQuoteQty)
        )
            ? orderOrReport.orderQuoteQty
            : (
                // Usually this field is empty, but present only, when it must to me used first.
                !isNil(orderOrReport._orderBaseQty)
                    ? orderOrReport._orderBaseQty
                    : orderOrReport.orderQty
            )
        : orderOrReport.orderQty;
}

export function getQtyAndQtyAssetTypeFromOrder(
    security: Security,
    orderOrReport: OrderOrReportForQtyMultiCurrency,
    qtyMultiCurrencyInfo?: OrderQtyMultiCurrencyInfo,
    bypassNativeSupport: boolean = false,
    forcedQtyAssetType?: AssetType
): {qty: number; qtyAssetType: AssetType} {
    qtyMultiCurrencyInfo = qtyMultiCurrencyInfo
        || getOrderQtyMultiCurrencyInfoForOrder(orderOrReport as NewOrder, security);

    const qtyAssetType = getOrderQtyAssetType(
        security,
        orderOrReport,
        qtyMultiCurrencyInfo,
        bypassNativeSupport,
        forcedQtyAssetType
    );
    const qty = getQtyFromOrder(
        security,
        orderOrReport,
        qtyMultiCurrencyInfo,
        bypassNativeSupport,
        forcedQtyAssetType
    );

    return {qty, qtyAssetType};
}
